import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Search, Building2, Users, Warehouse, Target, Landmark } from 'lucide-react'
import { useStore } from '@/store/useStore'
import { contactName } from '@/lib/selectors'

interface Hit { kind: string; id: string; label: string; sub?: string; to: string; icon: typeof Search }

export function GlobalSearch() {
  const data = useStore((s) => s.data)
  const navigate = useNavigate()
  const [q, setQ] = useState('')
  const [open, setOpen] = useState(false)
  const [active, setActive] = useState(0)
  const inputRef = useRef<HTMLInputElement>(null)
  const boxRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        inputRef.current?.focus()
      }
    }
    const onClick = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false)
    }
    window.addEventListener('keydown', onKey)
    window.addEventListener('mousedown', onClick)
    return () => {
      window.removeEventListener('keydown', onKey)
      window.removeEventListener('mousedown', onClick)
    }
  }, [])

  const hits = useMemo<Hit[]>(() => {
    const term = q.trim().toLowerCase()
    if (!term) return []
    const out: Hit[] = []
    for (const a of data.accounts)
      if (a.name.toLowerCase().includes(term))
        out.push({ kind: 'Account', id: a.id, label: a.name, sub: a.type, to: `/accounts/${a.id}`, icon: Building2 })
    for (const c of data.contacts) {
      if (c.archived) continue // admin-only, via the Contacts Archived view
      const n = contactName(c)
      if (n.toLowerCase().includes(term) || (c.title ?? '').toLowerCase().includes(term))
        out.push({ kind: 'Contact', id: c.id, label: n, sub: c.title, to: `/contacts/${c.id}`, icon: Users })
    }
    for (const p of data.properties)
      if (p.name.toLowerCase().includes(term) || (p.externalIds?.lcode ?? '').toLowerCase().includes(term))
        out.push({ kind: 'Property', id: p.id, label: p.name, sub: p.externalIds?.lcode, to: `/properties/${p.id}`, icon: Warehouse })
    for (const o of data.opportunities)
      if (o.name.toLowerCase().includes(term))
        out.push({ kind: 'Opportunity', id: o.id, label: o.name, sub: o.type, to: `/opportunities/${o.id}`, icon: Target })
    for (const f of data.funds)
      if (f.name.toLowerCase().includes(term))
        out.push({ kind: 'Fund', id: f.id, label: f.name, sub: f.vintage, to: `/funds/${f.id}`, icon: Landmark })
    return out.slice(0, 12)
  }, [q, data])

  function go(h: Hit) {
    navigate(h.to)
    setQ('')
    setOpen(false)
    inputRef.current?.blur()
  }

  return (
    <div className="gsearch" ref={boxRef}>
      <Search size={16} className="gsearch-icon" />
      <input
        ref={inputRef}
        className="gsearch-input"
        placeholder="Find or ask…"
        value={q}
        onChange={(e) => { setQ(e.target.value); setOpen(true); setActive(0) }}
        onFocus={() => setOpen(true)}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown') { e.preventDefault(); setActive((a) => Math.min(a + 1, hits.length - 1)) }
          else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)) }
          else if (e.key === 'Enter' && hits[active]) go(hits[active])
          else if (e.key === 'Escape') setOpen(false)
        }}
      />
      {/* ⌘K badge removed (Windows shop) — Ctrl+K / ⌘K still focus search. */}
      {open && q.trim() && (
        <div className="gsearch-menu">
          {hits.length === 0 && <div className="gsearch-empty">No matches for “{q}”.</div>}
          {hits.map((h, i) => {
            const Icon = h.icon
            return (
              <button
                key={`${h.kind}-${h.id}`}
                className={`gsearch-hit${i === active ? ' active' : ''}`}
                onMouseEnter={() => setActive(i)}
                onClick={() => go(h)}
              >
                <Icon size={15} className="gsearch-hit-icon" />
                <span className="gsearch-hit-label truncate">{h.label}</span>
                {h.sub && <span className="gsearch-hit-sub truncate">{h.sub}</span>}
                <span className="gsearch-hit-kind">{h.kind}</span>
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}
